import Icon from "@/v2-ui/icon"
import Link from "@/v2-backoffice-client/components/link"
import ManualLink from "@/v2-backoffice-client/components/manual/ManualLink"
import { HUBSPOT_BASE_URL } from "@/v2-backoffice-client/constants"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { CUSTOMER_CRM_MERGES_HREF } from "@/v2-backoffice-client/customer/customer.constants"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import { formatMergeSurvivor } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"

export type CustomerMergeLeftoverNoticeProps = {
  customer: CustomerFragment
}

/**
 * The notice at the top of a customer panel whose HubSpot company was
 * merged into another company (Customer.MergedInto): the row is what the
 * merge left behind, which is why it has a bare id for a name, an empty CRM
 * card, or a team and a user that belong elsewhere. Says where the merge
 * went and what the Backoffice does about it - its people move onto the
 * surviving customer on their own when the money side is clear, or the
 * CRM Merges board says what a person has to decide - with the ways on
 * next to it: the surviving customer's panel, the CRM Merges board, and
 * HubSpot. A row an operator kept as a separate customer says so and asks
 * for nothing. Renders nothing for a customer HubSpot still has, so the
 * caller mounts it unconditionally.
 */
function CustomerMergeLeftoverNotice(props: CustomerMergeLeftoverNoticeProps) {
  const { customer } = props
  const mergedInto = customer.MergedInto

  if (!mergedInto) return null

  const survivor = mergedInto.SurvivingCustomer
  const holds = [
    customer.TenantCount > 0
      ? `${customer.TenantCount} team${customer.TenantCount === 1 ? "" : "s"}`
      : null,
    customer.UserCount > 0
      ? `${customer.UserCount} user${customer.UserCount === 1 ? "" : "s"}`
      : null,
  ].filter((part): part is string => part !== null)
  const holdsVerb =
    customer.TenantCount + customer.UserCount === 1 ? "belongs" : "belong"

  const tone = mergedInto.IsKeptSeparate
    ? "border-border bg-muted/40"
    : "border-destructive/50 bg-destructive/10"
  const iconClass = mergedInto.IsKeptSeparate
    ? "text-muted-foreground"
    : "text-destructive"

  return (
    <section
      role="status"
      className={`flex flex-col gap-2 rounded-md border px-3 py-2.5 text-sm ${tone}`}
      data-feature-code="UI_BackofficeCustomer_MergeLeftover_Notice"
    >
      <p className="flex items-start gap-2">
        <Icon
          src={mergedInto.IsKeptSeparate ? "code-merge" : "triangle-warning"}
          size={14}
          className={`mt-0.5 shrink-0 ${iconClass}`}
          aria-hidden
        />
        <span>
          <span className="font-medium">
            Merged away in HubSpot: company {mergedInto.MergedExternalCompanyId}{" "}
            was merged into {formatMergeSurvivor(mergedInto)}.
          </span>{" "}
          <span className="text-muted-foreground">
            {mergedInto.IsKeptSeparate
              ? "An operator kept this row as a separate customer: it stays as it is, without a CRM company, and can be picked for teams and users like any other."
              : `This row is what the merge left behind. ${
                  holds.length > 0
                    ? `Its ${holds.join(" and ")} ${holdsVerb} on the surviving customer; the Backoffice moves them there on its own when one row holds the licence or billing account, and CRM Merges says what a person has to decide otherwise.`
                    : "Nothing is on it any more; pick the surviving customer for anything new."
                }${
                  !survivor
                    ? " The sync links the surviving company to a customer within the hour."
                    : ""
                }`}
          </span>
        </span>
      </p>

      <div className="flex flex-wrap items-center gap-2 pl-6">
        {survivor?.ExternalCustomerId && (
          <Link
            variant="unstyled"
            iconSrc="building"
            iconSize={12}
            to={`>>${buildCustomerViewLink(survivor.ExternalCustomerId)}`}
            className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
            data-feature-code="UI_BackofficeCustomer_MergeLeftover_Survivor"
          >
            Open {survivor.Name ?? "the surviving customer"}
          </Link>
        )}

        <Link
          variant="unstyled"
          iconSrc="code-merge"
          iconSize={12}
          to={CUSTOMER_CRM_MERGES_HREF}
          className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
          title="Every HubSpot company merge the sync has seen, what became of it here, and the choices: keep one row, or keep both."
          data-feature-code="UI_BackofficeCustomer_MergeLeftover_Merges"
        >
          Review CRM merges
        </Link>

        <Link
          variant="unstyled"
          iconSrc="arrow-up-right-from-square"
          iconSize={12}
          external
          to={`${HUBSPOT_BASE_URL}/${mergedInto.SurvivingExternalCompanyId}`}
          className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
          data-feature-code="UI_BackofficeCustomer_MergeLeftover_HubSpot"
        >
          Open in HubSpot
        </Link>

        <ManualLink manual="crmMerges" size="xs" />
      </div>
    </section>
  )
}

export default CustomerMergeLeftoverNotice
